import React, { useEffect, useState } from 'react';
import propTypes from 'prop-types';
import { connect } from 'unistore/react';
import isEmpty from 'lodash/fp/isEmpty';
import pick from 'lodash/fp/pick';
import reject from 'lodash/fp/reject';

import { actions } from '../store/PlanStore';
import {
  selectEnableEditFlow,
  selectLoadingInitialData,
  selectShowAddOnsPage,
} from '../store/selectors';

import Nav from './Nav';
import Landing from './Landing';
import PodsPicker from './PodsPicker';
import AddOnsPicker from './AddOnsPicker';
import PlanCart from './PlanCart';
import Review from './Review';
import { useHistoryStepsSynchronization } from '../hooks/useHistoryStepsSynchronization';
import { TranslatedMessage } from 'common/components/Intl';
import LoadingSplash from 'common/components/LoadingSplash';

export function BuildYourPlanComponent({
  error,
  errorCodes,
  fetchInitialData,
  landingContent,
  loadingInitialData,
  addOnsContent,
  redirectToCheckout,
  showAddOnsPage,
  checkout,
}) {
  const steps = reject(isEmpty)([
    {
      slug: 'landing',
      component: Landing,
      content: landingContent,
      showCart: false,
    },
    { slug: 'pods', component: PodsPicker, showCart: true },
    showAddOnsPage && !isEmpty(addOnsContent.productDisplays)
      ? {
          slug: 'add-ons',
          component: AddOnsPicker,
          content: addOnsContent,
          showCart: true,
        }
      : {},
    { slug: 'review', component: Review, showCart: false },
  ]);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    if (redirectToCheckout) checkout();
  }, [checkout, redirectToCheckout]);

  useHistoryStepsSynchronization({
    steps,
    currentStep,
    setCurrentStep,
    loadingInitialData,
  });

  const navigateTo = stepSlug => {
    const stepIndex = steps.findIndex(step => step.slug === stepSlug);
    setCurrentStep(stepIndex);
  };

  const navigateToNextStep = () => setCurrentStep(step => step + 1);

  const { component: StepComponent, content, showCart } = steps[currentStep];

  const isNavigatingToLastStep = currentStep === steps.length - 2;

  return (
    <div className="build-your-plan">
      <Nav navigateTo={navigateTo} />
      <div className="content-wrapper">
        {loadingInitialData ? (
          <LoadingSplash />
        ) : (
          <>
            <StepComponent
              navigateTo={navigateTo}
              navigateToNextStep={navigateToNextStep}
              {...content}
              error={error}
              errorCodes={errorCodes}
            />
            {showCart ? (
              <PlanCart
                navigateToNextStep={navigateToNextStep}
                nextButtonContent={
                  <TranslatedMessage
                    id={
                      isNavigatingToLastStep
                        ? 'plan_builder.cart.review'
                        : 'plan_builder.cart.next'
                    }
                  />
                }
              />
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

BuildYourPlanComponent.propTypes = {
  landingContent: propTypes.object.isRequired,
  error: propTypes.object,
  errorCodes: propTypes.object.isRequired,
  loadingInitialData: propTypes.bool,
  pods: propTypes.array.isRequired,
  fetchInitialData: propTypes.func.isRequired,
  redirectToCheckout: propTypes.bool,
  checkout: propTypes.func.isRequired,
  enableEditFlow: propTypes.bool,
  showAddOnsPage: propTypes.bool,
};

export default connect(
  state => ({
    ...pick(['pods', 'redirectToCheckout', 'error'])(state),
    enableEditFlow: selectEnableEditFlow(state),
    loadingInitialData: selectLoadingInitialData(state),
    showAddOnsPage: selectShowAddOnsPage(state),
  }),
  actions
)(BuildYourPlanComponent);
